import React, { useState, useMemo, useEffect } from "react";
import { validatePhoneNumber } from "../../../../../utils/common";
import IC_VISIBLE from "@/assets/login/ic_visible.svg";
import IC_INVISIBLE from "@/assets/login/ic_invisible.svg";
import IC_BACK from "@/assets/login/ic_back.svg";
import { getUserInfoByMobile } from "@/services/api_user";
import useLoginStore from "@/store/login.store";
import CommonLoading from "@/component/CommonLoading";
import { registerByPassword } from "@/services/api_user";
import { message } from "antd";
import { encryptWithSha256 } from "@/utils/crypto";

interface SecretSettingProps {
  onConfirm?: () => void;
  onCancel: () => void;
}

enum UserType {
  newUser = 0,
  oldUser = 1,
  noAccess = 2,
}

// 密码设置页面组件
const SecretSetting: React.FC<SecretSettingProps> = ({
  onConfirm,
  onCancel,
}) => {
  // 组件内部状态
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // 验证状态
  const [mobileValid, setMobileValid] = useState(true);
  const [passwordValid, setPasswordValid] = useState(true);
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(true);
  
  // 用户类型检查状态
  const [checkingUser, setCheckingUser] = useState(false);
  const [userType, setUserType] = useState(UserType.newUser);
  
  // 密码可视状态
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mobile, setMobile } = useLoginStore();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const checkUserInfo = async (mobile: string) => {
    try {
      const res = await getUserInfoByMobile(mobile);
      if(res.company === null){
        return UserType.noAccess;
      }else{
        return UserType.oldUser;
      }
    } catch (error) {
      return UserType.newUser;
    }
  }

  // 手机号输入处理
  const onMobileInput = async (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setMobile(numericValue);
    
    // 重置用户类型检查状态
    setUserType(UserType.newUser);
    // 验证手机号
    if (numericValue) {
      const isValid = validatePhoneNumber(numericValue);
      setMobileValid(isValid);
      
      // 如果手机号合法，调用 getIsNewUser 接口
      if (isValid) {
        setCheckingUser(true);
        const userType = await checkUserInfo(numericValue);
        setUserType(userType);
      }
    } else {
      setMobileValid(true); // 空值时不显示错误
    }
  };

  const isPasswordValid = (value: string) => {
    // 密码验证：长度 8-16 位，且必须包含字母、数字、符号中的至少两种
    if (value) {
      const lengthValid = value.length >= 8 && value.length <= 16;
      const hasLetter = /[a-zA-Z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSymbol = /[^a-zA-Z0-9]/.test(value);
      const typeCount = [hasLetter, hasNumber, hasSymbol].filter(
        Boolean
      ).length;
      const isValid = lengthValid && typeCount >= 2;
      return isValid;
    } else {
      return true;
    }
  }

  // 密码输入处理
  const onPasswordChange = (value: string) => {
    setPassword(value);
    setPasswordValid(true);
    // 如果确认密码已输入，重新验证确认密码
    if (confirmPassword) {
      setConfirmPasswordValid(value === confirmPassword);
    }
  };

  const onPasswordBlur = () => {
    setPasswordValid(isPasswordValid(password));
  };

  // 确认密码输入处理
  const onConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    
    // 确认密码验证：与密码一致
    if (value) {
      const isValid = value === password;
      setConfirmPasswordValid(isValid);
    } else {
      setConfirmPasswordValid(true);
    }
  };

  // 确认按钮禁用状态
  const isConfirmDisabled = useMemo(() => {
    return !mobile.trim() || 
           !password.trim() || 
           !confirmPassword.trim() || 
           !mobileValid || 
           !passwordValid || 
           !confirmPasswordValid ||
           userType !== UserType.newUser ||
           checkingUser;
  }, [mobile, password, confirmPassword, mobileValid, passwordValid, confirmPasswordValid, userType, checkingUser]);

  // 确认处理
  const handleConfirm = async () => {
    if (confirmLoading) return;
    
    // 最终验证
    if (!validatePhoneNumber(mobile)) {
      setMobileValid(false);
      return;
    }

    if (!isPasswordValid(password)) {
      setPasswordValid(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setConfirmPasswordValid(false);
      return;
    }

    setConfirmLoading(true);
    
    try {
      // 对密码进行 SHA256 加密
      const encryptedPassword = encryptWithSha256(password);
      const encryptedConfirmPassword = encryptWithSha256(confirmPassword);
      await registerByPassword({ 
        mobile, 
        password: encryptedPassword, 
        confirmPassword: encryptedConfirmPassword 
      });
      const userType = await checkUserInfo(mobile);
      setUserType(userType);
      if(userType === UserType.noAccess){
        message.warning("该账户未绑定组织，请先联系管理员");
        return;
      }
      message.success("设置密码成功");
      onConfirm?.();
    } catch (e) {
      console.log("设置密码失败:", e);
      message.warning("设置密码失败");
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    if (mobile && validatePhoneNumber(mobile)) {
      setCheckingUser(true);
      checkUserInfo(mobile)
        .then((userType) => {
          setUserType(userType);
        })
        .finally(() => {
          setCheckingUser(false);
        });
    } else {
      // 手机号无效时重置状态
      setUserType(UserType.newUser);
      setCheckingUser(false);
      setMobileValid(false);
    }
  }, [mobile]);

  return (
    <div className="h-full bg-white flex flex-col flex-items-center px-9 relative left-1/2 top-1/2 -translate-1/2">
      <div className="w-90 mt-6 mb-6">
        <button
          className="flex flex-items-center text-#191D26 text-size-4 cursor-pointer border-0 bg-transparent"
          onClick={onCancel}
        >
          <img src={IC_BACK} alt="back" />
          <span className="ml-1">返回</span>
        </button>
      </div>

      <div className="w-86 mb-8">
        <h1 className="text-#1F2533 text-size-6 font-500 select-none">
          设置新账户密码
        </h1>
      </div>

      <div className="w-86 flex flex-col">
        <div className="h-52">
          {/* 手机号输入 */}
          <div className="h-16 mb-2">
            <div
              className="h-12 bg-#F4F6F9 rd-2.5 border-1 border-solid py-1 px-3 transition-colors border-transparent focus-within:border-#4981F2"
            >
              <input
                className="w-full h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-3.5 placeholder:font-400"
                placeholder="手机号"
                value={mobile}
                onChange={(e) => onMobileInput(e.target.value)}
                maxLength={11}
              />
            </div>
            {!mobileValid && mobile && (
              <div className="h-5 text-size-3 text-#FF584C ml-3 mt-1">
                无效手机号
              </div>
            )}
            {mobileValid && mobile && checkingUser && (
              <div className="h-5 flex flex-items-center ml-3">
                <CommonLoading className="w-4 h-4 mr-2" />
              </div>
            )}
            {mobileValid && mobile && !checkingUser && userType !== UserType.newUser && (
              <div className="h-5 text-size-3 text-#FF584C ml-3 mt-1">
                {userType === UserType.noAccess ? "该账户未绑定组织，请先联系管理员" : "该账号已登录过，无需设置密码"}
              </div>
            )}
          </div>

          {userType === UserType.newUser && (
            <>
              {/* 密码输入 */}
              <div className="h-16 mb-2">
                <div
                  className="h-12 bg-#F4F6F9 rd-2.5 border-1 border-solid py-1 px-3 transition-colors flex flex-items-center border-transparent focus-within:border-#4981F2"
                >
                  <input
                    type={showPassword ? "text" : "password"}
                    className="flex-1 h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-3.5 placeholder:font-400"
                    placeholder="设置新密码"
                    value={password}
                    onChange={(e) => onPasswordChange(e.target.value)}
                    onBlur={onPasswordBlur}
                    maxLength={20}
                  />
                  <button
                    type="button"
                    className="ml-2 w-6 h-6 flex flex-items-center flex-justify-center cursor-pointer border-0 bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <img src={IC_VISIBLE} alt="visible" />
                    ) : (
                      <img src={IC_INVISIBLE} alt="invisible" />
                    )}
                  </button>
                </div>
                {!passwordValid && password && (
                  <div className="h-5 text-size-3 text-#FF584C ml-3 mt-1">
                    输入密码需8-16位字母/数字/符号至少包含2种
                  </div>
                )}
              </div>

              {/* 确认密码输入 */}
              <div className="h-16">
                <div
                  className="h-12 bg-#F4F6F9 rd-2.5 border-1 border-solid py-1 px-3 transition-colors flex flex-items-center border-transparent focus-within:border-#4981F2"
                >
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="flex-1 h-10 border-0 bg-transparent text-size-4 focus:outline-none placeholder:text-#A1A7B2 placeholder:text-size-3.5 placeholder:font-400"
                    placeholder="再次输入新密码"
                    value={confirmPassword}
                    onChange={(e) => onConfirmPasswordChange(e.target.value)}
                    maxLength={20}
                  />
                  <button
                    type="button"
                    className="ml-2 w-6 h-6 flex flex-items-center flex-justify-center cursor-pointer border-0 bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <img src={IC_VISIBLE} alt="visible" />
                    ) : (
                      <img src={IC_INVISIBLE} alt="invisible" />
                    )}
                  </button>
                </div>
                {!confirmPasswordValid && confirmPassword && (
                  <div className="h-5 text-size-3 text-#FF584C ml-3 mt-1">
                    两次密码输入不一致
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 确定按钮 */}
        <div className="flex flex-justify-between space-x-4 mt-40">
          <button
            disabled={isConfirmDisabled}
            className={`flex-1 h-10 bg-#293143 rd-2.5 flex flex-items-center flex-justify-center text-size-3.5 border-none ${
              isConfirmDisabled
                ? "text-#6B7280 cursor-not-allowed"
                : "text-#FFFFFF cursor-pointer"
            }`}
            onClick={handleConfirm}
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecretSetting;
